import { apiClient } from '../ApiClient.js';
import { getEfemLoadPortEventsUrl, getFingerprintInferenceBatchEventsUrl } from './urls.js';

/**
 * SSE stream for load-port binding updates (passive barcode scans).
 * Runs until signal is aborted (component unmount).
 */
export async function subscribeEfemLoadPortEvents(
  gatewaySerialNumber: string,
  onEvent: () => void,
  signal: AbortSignal,
): Promise<void> {
  try {
    const token = await apiClient.getFreshAccessToken();
    const url = getEfemLoadPortEventsUrl(gatewaySerialNumber);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    });

    if (!response.ok || !response.body) {
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop()!;
        for (const part of parts) {
          if (part.startsWith(':')) continue;
          const dataLine = part.split('\n').find((l) => l.startsWith('data:'));
          if (dataLine) {
            onEvent();
          }
        }
      }
    } catch (err) {
      if (!signal.aborted) {
        console.warn('EFEM load port event stream ended', err);
      }
    } finally {
      reader.releaseLock();
    }
  } catch (err) {
    if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
      return;
    }
    if (err instanceof Error && err.name === 'AbortError') {
      return;
    }
    throw err;
  }
}

/** Consumes fingerprint-inference batch SSE until aborted; calls onEvent for each data line. */
export async function consumeFingerprintInferenceBatchSse(
  batchId: string,
  signal: AbortSignal,
  onEvent: () => void,
): Promise<void> {
  const token = await apiClient.getSseAccessToken();
  const response = await fetch(getFingerprintInferenceBatchEventsUrl(batchId), {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Fingerprint inference SSE failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop()!;
    for (const part of parts) {
      for (const line of part.split('\n')) {
        if (line.startsWith('data:')) {
          onEvent();
        }
      }
    }
  }
}
